/**
 * Which backend contract a client speaks, and how that contract reports errors.
 *
 * Deliberately free of axios and authService imports: this is pure config and
 * payload logic, and keeping it dependency-free means it can be unit tested.
 * (CRA 5's Jest does not transform node_modules, and axios v1's entry point is
 * ESM, so anything importing axios cannot currently be tested.)
 *
 *   "legacy"  Bajaj / Ergo — POST, form-encoded, /assessment_new
 *   "v2"      Hegic        — GET, query params, /assessment, and failures
 *                            returned as HTTP 200 with an Error field
 *
 * See HEGIC_API.md for the full contract.
 */

import pageConfig from "../config/caseDetailsConfig.json";
import { CLIENTS } from "../config/clients";

const registry = CLIENTS;

/**
 * The contract for the client the user is logged in as.
 *
 * Resolved at call time, not module load: the client is only known after login.
 * Session first, then the portal config, then the build-time default — same
 * precedence as getEffectiveSourceName() in apiService.
 */
export const getApiGeneration = () => {
  let loggedIn = null;
  try {
    loggedIn = localStorage.getItem("source_name");
  } catch { /* localStorage unavailable */ }

  const entry = loggedIn ? registry[loggedIn] : null;
  if (entry?.apiGeneration) return entry.apiGeneration;

  try {
    const stored = localStorage.getItem("portalConfig");
    if (stored) {
      const gen = JSON.parse(stored)?.configuration?.client?.apiGeneration;
      if (gen) return gen;
    }
  } catch { /* malformed or unavailable */ }

  // A registered client with no explicit apiGeneration is legacy, as is an
  // unknown one — never guess v2, since the wrong verb fails confusingly.
  const active = pageConfig.client?.active;
  return registry[active]?.apiGeneration || "legacy";
};

/**
 * Guard a v2 response.
 *
 * v2 answers 200 even when the request was wrong, putting the reason in
 * Error / Message / msg, so the status code alone cannot be trusted. Throws so
 * callers handle it in the same catch block as a transport failure.
 *
 * Careful with false positives: a healthy list response contains `Status: 200`
 * and `previous: -1`, neither of which means failure.
 */
export const assertV2Ok = (body, context) => {
  const reason = body?.Error || body?.error || body?.Message || body?.message;
  if (typeof reason === "string" && reason) {
    throw new Error(`${context}: ${reason}`);
  }
  if (body?.status === -1 || body?.status === "-1") {
    throw new Error(`${context}: request rejected (status -1)`);
  }
  return body;
};
